import React, { useEffect, useRef, useMemo } from 'react';
import type { Message } from '../../types/message';
import { formatMessageTime } from '../../utils/dateUtils';
import { useAuth } from '../../auth/useAuth';
import clsx from 'clsx';
import { useLanguage } from '../../i18n/useLanguage';
import { UserAvatar } from '../common/UserAvatar';
import './MessageList.css';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  typingUsers?: string[];
}

interface GroupedMessage extends Message {
  isGrouped?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  typingUsers = [],
}) => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sort messages chronologically (oldest first) and group consecutive messages from the same sender (within 5 minutes)
  const groupedMessages = useMemo(() => {
    if (messages.length === 0) return [];
    
    // Sort messages by createdAt ascending (oldest first)
    const sortedMessages = [...messages].sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      return timeA - timeB;
    });
    
    const grouped: GroupedMessage[] = [];
    const GROUP_TIME_MS = 5 * 60 * 1000; // 5 minutes
    
    for (let i = 0; i < sortedMessages.length; i++) {
      const currentMessage = sortedMessages[i];
      const prevMessage = i > 0 ? sortedMessages[i - 1] : null;
      
      const isGrouped = prevMessage &&
        prevMessage.senderId === currentMessage.senderId &&
        new Date(currentMessage.createdAt).getTime() - new Date(prevMessage.createdAt).getTime() < GROUP_TIME_MS;
      
      grouped.push({
        ...currentMessage,
        isGrouped: !!isGrouped,
      });
    }
    
    return grouped;
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive or typing indicator appears
  useEffect(() => {
    if (messagesEndRef.current && (messages.length > 0 || typingUsers.length > 0)) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, typingUsers.length]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="message-list">
        <div className="message-list-loading">{t('messages.list.loading')}</div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="message-list">
        <div className="message-list-empty">{t('messages.list.empty')}</div>
      </div>
    );
  }

  return (
    <div className="message-list" ref={containerRef}>
      {hasMore && onLoadMore && (
        <div className="message-list-load-more">
          <button onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? t('common.loading') : t('messages.list.loadOlder')}
          </button>
        </div>
      )}
      {groupedMessages.map((message) => {
        const isOwnMessage = user?.sub === message.senderId;
        return (
          <div
            key={message.id}
            className={clsx(
              'message-item',
              isOwnMessage ? 'message-item--own' : 'message-item--other',
              message.isGrouped && 'message-item-grouped'
            )}
          >
            {!isOwnMessage && (
              <div className="message-item-avatar-col">
                {!message.isGrouped && (
                  <UserAvatar
                    userId={message.senderId}
                    username={message.senderUsername}
                    size={28}
                  />
                )}
              </div>
            )}
            <div className="message-item-body">
              {!isOwnMessage && !message.isGrouped && (
                <div className="message-item-sender">{message.senderUsername}</div>
              )}
              <div className="message-item-content">{message.content}</div>
              <div className="message-item-meta">
                <span className="message-item-time" title={message.createdAt}>
                  {formatMessageTime(message.createdAt, language)}
                </span>
                {isOwnMessage && (
                  <span
                    className={clsx('message-item-read', message.isRead && 'double-check')}
                    title={message.isRead ? t('messages.list.statusRead') : t('messages.list.statusSent')}
                  >
                    ✓
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {/* Typing indicator bubble */}
      {typingUsers.length > 0 && (
        <div className="message-item message-item--other message-item-grouped">
          <div className="message-item-avatar-col" />
          <div className="message-item-body">
            <div className="typing-indicator" title={typingUsers.join(', ')}>
              <span /><span /><span />
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};
